import React, { useCallback, useEffect, useRef, useState } from 'react';

function ChevronUpIcon({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 15l6-6 6 6" />
    </svg>
  );
}

function ChevronDownIcon({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

const railByTone = {
  dark: 'border-white/10 bg-gray-950/70 text-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.45)] backdrop-blur-md',
  light:
    'border-gray-200/80 bg-white/80 text-gray-700 shadow-[0_8px_30px_rgba(15,23,42,0.12)] backdrop-blur-md',
};

const hitByTone = {
  dark: 'hover:bg-white/10 active:bg-white/15 disabled:opacity-30',
  light: 'hover:bg-gray-900/5 active:bg-gray-900/10 disabled:opacity-30',
};

/**
 * Slim glass scroll rail on the right (not circular buttons).
 * Stays fixed in the visible pane. At bottom only up; at top only down.
 */
export function ScrollEdgeButtons({ scrollRef, className = '', tone = 'dark' }) {
  const [canScroll, setCanScroll] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const railClass = railByTone[tone] || railByTone.dark;
  const hitClass = hitByTone[tone] || hitByTone.dark;

  const update = useCallback(() => {
    const el = scrollRef?.current;
    if (!el) {
      setCanScroll(false);
      setAtTop(true);
      setAtBottom(true);
      return;
    }
    const maxScroll = el.scrollHeight - el.clientHeight;
    const scrollable = maxScroll > 4;
    const top = el.scrollTop <= 4;
    const bottom = el.scrollTop >= maxScroll - 4;
    setCanScroll(scrollable);
    setAtTop(!scrollable || top);
    setAtBottom(!scrollable || bottom);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    window.addEventListener('resize', update);
    const id = window.setTimeout(update, 120);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
      window.removeEventListener('resize', update);
      window.clearTimeout(id);
    };
  }, [scrollRef, update]);

  const scrollToTop = () => {
    scrollRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    const el = scrollRef?.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  if (!canScroll) return null;

  const showUp = !atTop;
  const showDown = !atBottom;
  if (!showUp && !showDown) return null;

  return (
    <div
      className={`pointer-events-none absolute right-2 top-1/2 z-50 -translate-y-1/2 ${className}`}
      aria-label="Scroll page"
    >
      <div
        className={`pointer-events-auto flex w-9 flex-col overflow-hidden rounded-2xl border ${railClass}`}
      >
        {showUp ? (
          <button
            type="button"
            onClick={scrollToTop}
            className={`flex h-11 w-full items-center justify-center transition-colors ${hitClass}`}
            title="Scroll to top"
            aria-label="Scroll to top"
          >
            <ChevronUpIcon className="h-4 w-4" />
          </button>
        ) : null}
        {showUp && showDown ? (
          <div className="mx-2 h-px bg-current opacity-15" aria-hidden="true" />
        ) : null}
        {showDown ? (
          <button
            type="button"
            onClick={scrollToBottom}
            className={`flex h-11 w-full items-center justify-center transition-colors ${hitClass}`}
            title="Scroll to bottom"
            aria-label="Scroll to bottom"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Non-scrolling shell: scroll area fills the shell; arrows stay pinned in view.
 */
export function ScrollableRightPane({
  children,
  className = '',
  scrollClassName = '',
  tone = 'dark',
  as: Component = 'div',
}) {
  const scrollRef = useRef(null);
  return (
    <Component className={`relative min-h-0 overflow-hidden ${className}`}>
      <div
        ref={scrollRef}
        className={`absolute inset-0 overflow-y-auto ${scrollClassName}`}
      >
        {children}
      </div>
      <ScrollEdgeButtons scrollRef={scrollRef} tone={tone} />
    </Component>
  );
}
